// src/pages/admin/RoleScopes.tsx
import React, { useState, useEffect } from 'react';
import { Lock, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Select, Input } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface RoleScope {
  id: number;
  role_id: number;
  role_name: string;
  role_display_name: string;
  kategori_masalah_id: number;
  kategori_nama: string;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
}

interface KategoriMasalah {
  id: number;
  nama: string;
  color?: string;
}

const RoleScopes: React.FC = () => {
  const [roleScopes, setRoleScopes] = useState<RoleScope[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [kategoris, setKategoris] = useState<KategoriMasalah[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedKategori, setSelectedKategori] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [scopesRes, rolesRes, kategorisRes] = await Promise.all([
        api.get('/admin/role-scopes'),
        api.get('/admin/roles'),
        api.get('/admin/kategoris'),
      ]);

      setRoleScopes(scopesRes.data?.data || []);
      setRoles(rolesRes.data?.data || []);
      setKategoris(kategorisRes.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddScope = async () => {
    if (!selectedRole || !selectedKategori) {
      toast.error('Pilih role dan kategori');
      return;
    }

    try {
      await api.post('/admin/role-scopes', {
        role_id: selectedRole,
        kategori_masalah_id: selectedKategori,
      });

      toast.success('Role scope berhasil ditambahkan');
      setShowAddModal(false);
      setSelectedRole('');
      setSelectedKategori('');
      fetchData();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Gagal menambahkan role scope';
      toast.error(message);
    }
  };

  const handleDeleteScope = async (id: number) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }

    try {
      setDeleting(id);
      await api.delete(`/admin/role-scopes/${id}`);
      toast.success('Role scope berhasil dihapus');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      toast.error('Gagal menghapus role scope');
    } finally {
      setDeleting(null);
    }
  };

  const getKategoriColor = (kategoriName: string) => {
    const kategori = kategoris.find(k => k.nama === kategoriName);
    return kategori?.color || '#999';
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-in">
        <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Role Scopes' }]} />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-main mx-auto mb-3"></div>
            <p className="text-sm text-neutral-text-secondary">Memuat data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Admin', href: '/admin' }, { label: 'Role Scopes' }]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Role Scopes</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Atur akses kategori untuk setiap role dalam validasi Level 2</p>
        </div>
        <Button
          variant="success"
          size="sm"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => setShowAddModal(true)}
        >
          Tambah Scope
        </Button>
      </div>

      {/* Info Card */}
      <Card className="!bg-info/10 border border-info">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-info mb-1">Informasi Role Scopes</p>
            <p className="text-sm text-neutral-text-secondary">
              Tentukan kategori masalah apa saja yang dapat divalidasi oleh setiap role pada Level 2.
              Admin secara otomatis memiliki akses ke semua kategori.
            </p>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-border bg-neutral-bg-secondary">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Role</th>
                <th className="px-4 py-3 text-left font-bold">Kategori Masalah</th>
                <th className="px-4 py-3 text-right font-bold">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-border">
              {roleScopes.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-neutral-text-secondary">
                    Belum ada role scope yang ditambahkan
                  </td>
                </tr>
              ) : (
                roleScopes.map((scope) => (
                  <tr key={scope.id} className="hover:bg-neutral-bg-secondary transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-neutral-text-primary">{scope.role_display_name}</p>
                        <p className="text-xs text-neutral-text-secondary font-mono">{scope.role_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getKategoriColor(scope.kategori_nama) }}
                        />
                        <span className="text-neutral-text-primary">{scope.kategori_nama}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant={deleteConfirm === scope.id ? 'danger' : 'outline'}
                        size="sm"
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => handleDeleteScope(scope.id)}
                        loading={deleting === scope.id}
                      >
                        {deleteConfirm === scope.id ? 'Yakin?' : 'Hapus'}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Tambah Role Scope"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-text-secondary">Pilih role dan kategori masalah</p>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-neutral-text-primary mb-1 block">
                Role
              </label>
              <Select
                value={selectedRole}
                onChange={setSelectedRole}
                placeholder="Pilih Role..."
                options={roles
                  .filter(r => r.name !== 'customer') // Filter out non-validator roles
                  .map(r => ({
                    value: r.id.toString(),
                    label: r.display_name,
                  }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-text-primary mb-1 block">
                Kategori Masalah
              </label>
              <Select
                value={selectedKategori}
                onChange={setSelectedKategori}
                placeholder="Pilih Kategori..."
                options={kategoris.map(k => ({
                  value: k.id.toString(),
                  label: k.nama,
                }))}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-border">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              Batal
            </Button>
            <Button variant="success" onClick={handleAddScope}>
              Tambah
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RoleScopes;
